import IconButton from '@/global/components/Button/IconButton'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import DevelopmentOnly from '@/global/components/DevelpmentOnly'
import Menu from '@/global/components/MenuV2'
import { CategoriaEnum } from '@acme/auth'
import { OrigensEnum } from '@acme/common'
import { SN } from '@acme/core'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    ArrowRightLeftIcon,
    CalendarArrowDownIcon,
    CalendarClockIcon,
    CalendarMinus2Icon,
    CalendarMinusIcon,
    ClockPlusIcon,
    EllipsisVerticalIcon,
    ShieldUserIcon,
    StarIcon,
} from 'lucide-react'
import React, { useMemo } from 'react'
import {
    ocorrencia,
    ocorrenciaTipoToPermission,
    registros,
    tabelaHorarios,
} from 'service/api'
import { useStore } from 'store'
import { useUpdateDigitacaoQueryData } from './pages/useDigitacaoData'
import { usePeriodoContrato } from './usePeriodoContrato'

function OpcoesDigitacao({
    row: {
        dia,
        registros: registrosRow,
        faltas,
        horasExtra,
        compensacaoManual,
    },
}: GridCellParams) {
    const {
        decoded: { categoria },
    } = useAuth()
    const { data = [] } = ocorrencia.useFetch()

    const { mutateAsync: excluirRegistro } = registros.mutations.useSoftDelete()
    const { mutateAsync: criarRegistro } = registros.mutations.useCreate()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const ocorrencias = data
        .filter(({ atalho }) => SN.isTrue(atalho))
        .sort((a, b) => a.descricao.localeCompare(b.descricao))

    const funcionario = useStore(state => state.employee)
    const empresa = useStore(state => state.company)

    // Verifica se o dia tem falta ou extra
    const temFalta = faltas && faltas !== '00:00'
    const temExtra = horasExtra && horasExtra !== '00:00'

    const isNotActive = usePeriodoContrato({ dia })

    if (isNotActive) return <InactiveButton />

    if (categoria === CategoriaEnum.Funcionario) {
        if (SN.isTrue(funcionario?.ajustesPonto))
            return (
                <Menu.Root position="right">
                    <Menu.Trigger>
                        <IconButton tabIndex={-1}>
                            <EllipsisVerticalIcon />
                        </IconButton>
                    </Menu.Trigger>
                    <Menu.List>
                        <Menu.Item href={`abono/adicionar/${dia}`}>
                            Adicionar abono
                        </Menu.Item>
                        <Menu.Item href={`registros/adicionar/${dia}`}>
                            Adicionar registro
                        </Menu.Item>
                    </Menu.List>
                </Menu.Root>
            )
        else return <InactiveButton />
    }

    const getCompensacaoManualUrl = () => {
        const params = new URLSearchParams()
        if (temFalta) params.append('tipo', 'falta')
        if (temExtra) params.append('tipo', 'extra')
        const queryString = params.toString()
        return `compensacao-manual/adicionar/${dia}${
            queryString ? `?${queryString}` : ''
        }`
    }

    const menuItems = [
        {
            to: `abono/adicionar/${dia}`,
            label: 'Abono',
            icon: <ClockPlusIcon className="mr-2 opacity-50" />,
            show: hasPermission(['create', 'Abonos']),
        },
        {
            to: `compensacao-feriado/adicionar/${dia}`,
            label: 'Compensação de feriado',
            icon: <CalendarArrowDownIcon className="mr-2 opacity-50" />,
            show: hasPermission(['create', 'CompensaçãoDeFeriados']),
        },
        {
            to: getCompensacaoManualUrl(),
            label: 'Compensação manual',
            icon: <CalendarClockIcon className="mr-2 opacity-50" />,
            show:
                !compensacaoManual &&
                hasPermission(['create', 'CompensaçãoManual']) &&
                SN.isTrue(funcionario?.RegrasCalculo?.compensacaoManual),
        },
        {
            to: `troca-dia-trabalho/adicionar/${dia}`,
            label: 'Troca dia trabalho',
            icon: <ArrowRightLeftIcon className="mr-2 opacity-50" />,
            show: hasPermission(['create', 'TrocaDeDiaDeTrabalho']),
        },
        {
            to: `falta-injustificada/adicionar/${dia}`,
            label: 'Falta injustificada',
            icon: <CalendarMinusIcon className="mr-2 opacity-50" />,
            show: hasPermission([
                'create',
                'FaltasJustificadasOuInjustificadas',
            ]),
        },
        {
            to: `falta-justificada/adicionar/${dia}`,
            label: 'Falta justificada',
            icon: <CalendarMinus2Icon className="mr-2 opacity-50" />,
            show: hasPermission([
                'create',
                'FaltasJustificadasOuInjustificadas',
            ]),
        },
        ...ocorrencias.map(ocorrencia => ({
            to: `afastamento/adicionar?ocorrenciaId=${ocorrencia.id}&dia=${dia}`,
            label: ocorrencia.descricao,
            icon: (
                <div
                    className="flex items-center"
                    title={`Atalho rápido\nDefinido na tela de ocorrências`}
                >
                    <StarIcon className="mr-2 text-yellow-400 cursor-help opacity-50" />
                </div>
            ),
            show:
                hasPermission([
                    'create',
                    ocorrenciaTipoToPermission(ocorrencia.tipo),
                ]) && !ocorrencia.disabled,
        })),
    ]

    const filteredItems = menuItems.filter(item => item.show)
    if (filteredItems.length > 0 && funcionario && empresa) {
        return (
            <Menu.Root
                position="right"
                arrow
                key={`${ocorrencias.length} + ${empresa?.id}`}
            >
                <Menu.Trigger>
                    <IconButton tabIndex={-1}>
                        <EllipsisVerticalIcon />
                    </IconButton>
                </Menu.Trigger>
                <Menu.List>
                    {filteredItems.map((item, index) => (
                        <Menu.Item key={index} href={item.to}>
                            {item.icon}
                            {item.label}
                        </Menu.Item>
                    ))}
                    <DevelopmentOnly>
                        {hasPermission(['delete', 'Registros'], () => (
                            <Menu.Item
                                onClick={async () =>
                                    Promise.all(
                                        registrosRow.map((registro: any) =>
                                            excluirRegistro({
                                                ...registro,
                                                motivo: 'NÃO FAÇA ISSO EM PRODUÇÃO',
                                            })
                                                .then(() =>
                                                    setQueryData(
                                                        'REGISTRO',
                                                        registro,
                                                        true,
                                                    ),
                                                )
                                                .catch(() => null),
                                        ),
                                    )
                                }
                                className="line-through"
                            >
                                <ShieldUserIcon className="mr-2 opacity-50" />
                                Limpar registros do dia
                            </Menu.Item>
                        ))}

                        {hasPermission(['create', 'Registros'], () => (
                            <Menu.Item
                                onClick={async () => {
                                    const tabelaDia =
                                        await tabelaHorarios.funcionario.get({
                                            funcionarioId: funcionario.id,
                                            dataInicial: dia,
                                            dataFinal: dia,
                                        })

                                    if (tabelaDia.at(0)?.TabelaHorario) {
                                        const daySemana = dayjs
                                            .utc(dia)
                                            .format('ddd')
                                            .toLowerCase()

                                        for await (const horario of Object.entries(
                                            tabelaDia[0].TabelaHorario,
                                        ).filter(
                                            ([key]) =>
                                                key.startsWith(
                                                    `${daySemana}Ent`,
                                                ) ||
                                                key.startsWith(
                                                    `${daySemana}Sai`,
                                                ),
                                        )) {
                                            const [, value] = horario as [
                                                string,
                                                string,
                                            ]

                                            if (value) {
                                                await criarRegistro({
                                                    hora: value,
                                                    data: dia,
                                                    empresaId: Number(
                                                        empresa.id,
                                                    ),
                                                    funcionarioId: Number(
                                                        funcionario.id,
                                                    ),
                                                    statusAprovacao: 'APROVADO',
                                                    origem: OrigensEnum.CONTROLE_ACESSO,
                                                    __typename: 'Registro',
                                                    entradaDiaSeguinte: 'N',
                                                    justificativa: '',
                                                })
                                                    .then(res => {
                                                        setQueryData(
                                                            'REGISTRO',
                                                            res,
                                                        )
                                                    })
                                                    .catch(() => null)
                                            }
                                        }
                                    }
                                }}
                                className="line-through"
                            >
                                <ShieldUserIcon className="mr-2 opacity-50" />
                                Preencher registros do dia
                            </Menu.Item>
                        ))}
                    </DevelopmentOnly>
                </Menu.List>
            </Menu.Root>
        )
    }

    return <InactiveButton />
}

function InactiveButton() {
    return (
        <IconButton disabled tabIndex={-1}>
            <EllipsisVerticalIcon />
        </IconButton>
    )
}

export default function OpcoesDigitacaoMemo(
    props: GridCellParams,
): React.JSX.Element | null {
    const funcionario = useStore(state => state.employee)

    return useMemo(() => {
        return <OpcoesDigitacao {...props} />
    }, [funcionario?.ajustesPonto, props.row.registros])
}
